// src\app\modules\subscription\utils\subscription-access.resolver.ts

import {
  SubscriptionAccessInput,
  SubscriptionAccessReason,
  SubscriptionAccessState,
  SubscriptionCheckoutMode,
} from '../models/subscription-access-state.model';

import { normalizeSubscriptionDate } from './subscription-date.utils';

const PENDING_CHECKOUT_STEPS = new Set<string>([
  'subscription_started',
  'subscription_plan_selected',
  'subscription_checkout_created',
  'subscription_checkout_redirected',
  'subscription_payment_started',
]);

/**
 * Resolve o estado centralizado de acesso à assinatura.
 *
 * Esta função:
 *
 * - não lê o Firestore;
 * - não altera o objeto recebido;
 * - não grava estados;
 * - não altera userJourneyStatus;
 * - não altera lastStep;
 * - não altera progresso de avaliação ou treinamento.
 */
export function resolveSubscriptionAccess(
  input: SubscriptionAccessInput,
  now: Date = new Date()
): SubscriptionAccessState {
  const subscriptionStatus = normalizeStatus(input.subscriptionStatus);
  const cycleStatus = normalizeStatus(input.subscriptionCycle?.status);
  const expiresAtValue = input.subscriptionCycle?.expiresAt;
  const expiresAt = normalizeSubscriptionDate(expiresAtValue);
  const lastStep = normalizeStatus(input.lastStep);

  const hasCycle = input.subscriptionCycle != null;
  const hasExpirationValue =
    expiresAtValue !== null && expiresAtValue !== undefined;

  const hasSubscriptionHistory = input.hasSubscriptionHistory;

  if (typeof hasSubscriptionHistory !== 'boolean') {
    return createState({
      kind: 'inconsistent',
      reason: 'MISSING_SUBSCRIPTION_HISTORY',
      hasSubscriptionHistory: false,
      expiresAt,
    });
  }

  if (!isValidDate(now)) {
    return createState({
      kind: 'inconsistent',
      reason: 'INVALID_EXPIRATION',
      hasSubscriptionHistory,
      expiresAt,
    });
  }

  if (subscriptionStatus === 'past_due') {
    return createState({
      kind: 'past_due',
      reason: 'PAYMENT_PAST_DUE',
      hasSubscriptionHistory,
      expiresAt,
    });
  }

  if (isPendingCheckoutStep(lastStep)) {
    return createState({
      kind: 'pending_checkout',
      reason: 'CHECKOUT_IN_PROGRESS',
      hasSubscriptionHistory,
      expiresAt,
    });
  }

  if (hasCycle && !hasExpirationValue) {
    return createState({
      kind: 'inconsistent',
      reason: 'MISSING_EXPIRATION',
      hasSubscriptionHistory,
      expiresAt: null,
    });
  }

  if (hasCycle && hasExpirationValue && expiresAt === null) {
    return createState({
      kind: 'inconsistent',
      reason: 'INVALID_EXPIRATION',
      hasSubscriptionHistory,
      expiresAt: null,
    });
  }

  if (isExpiredStatus(subscriptionStatus) || isExpiredStatus(cycleStatus)) {
    return createState({
      kind: 'expired',
      reason: 'CYCLE_STATUS_EXPIRED',
      hasSubscriptionHistory,
      expiresAt,
    });
  }

  if (expiresAt !== null && expiresAt.getTime() <= now.getTime()) {
    return createState({
      kind: 'expired',
      reason: 'CYCLE_DATE_EXPIRED',
      hasSubscriptionHistory,
      expiresAt,
    });
  }

  if (subscriptionStatus === 'active') {
    if (!hasCycle) {
      return createState({
        kind: 'inconsistent',
        reason: 'MISSING_CYCLE',
        hasSubscriptionHistory,
        expiresAt: null,
      });
    }

    if (cycleStatus !== 'active') {
      return createState({
        kind: 'inconsistent',
        reason: 'STATUS_CYCLE_CONFLICT',
        hasSubscriptionHistory,
        expiresAt,
      });
    }

    return createState({
      kind: 'active',
      reason: 'ACTIVE_CYCLE_VALID',
      hasSubscriptionHistory,
      expiresAt,
    });
  }

  if (isCanceledStatus(subscriptionStatus)) {
    if (!hasCycle) {
      return createState({
        kind: 'inconsistent',
        reason: 'MISSING_CYCLE',
        hasSubscriptionHistory,
        expiresAt: null,
      });
    }

    if (!isCanceledStatus(cycleStatus)) {
      return createState({
        kind: 'inconsistent',
        reason: 'STATUS_CYCLE_CONFLICT',
        hasSubscriptionHistory,
        expiresAt,
      });
    }

    return createState({
      kind: 'canceled_with_access',
      reason: 'CANCELED_CYCLE_VALID',
      hasSubscriptionHistory,
      expiresAt,
    });
  }

  if (
    subscriptionStatus === 'inactive' ||
    subscriptionStatus === null
  ) {
    if (hasCycle) {
      return createState({
        kind: 'inconsistent',
        reason: 'STATUS_CYCLE_CONFLICT',
        hasSubscriptionHistory,
        expiresAt,
      });
    }

    if (!hasSubscriptionHistory) {
      return createState({
        kind: 'never_subscribed',
        reason: 'NO_SUBSCRIPTION_HISTORY',
        hasSubscriptionHistory,
        expiresAt: null,
      });
    }

    return createState({
      kind: 'expired',
      reason: 'CYCLE_STATUS_EXPIRED',
      hasSubscriptionHistory,
      expiresAt: null,
    });
  }

  return createState({
    kind: 'inconsistent',
    reason: 'STATUS_CYCLE_CONFLICT',
    hasSubscriptionHistory,
    expiresAt,
  });
}

/**
 * Indica se o usuário possui acesso vigente aos recursos
 * protegidos pela assinatura.
 */
export function hasActiveSubscriptionAccess(
  input: SubscriptionAccessInput,
  now: Date = new Date()
): boolean {
  return resolveSubscriptionAccess(input, now).hasAccess;
}

/**
 * Indica se a assinatura anterior está expirada.
 */
export function isSubscriptionExpired(
  input: SubscriptionAccessInput,
  now: Date = new Date()
): boolean {
  return resolveSubscriptionAccess(input, now).isExpired;
}

/**
 * Indica se o usuário está no contexto de primeira contratação.
 */
export function isFirstSubscription(
  input: SubscriptionAccessInput,
  now: Date = new Date()
): boolean {
  return resolveSubscriptionAccess(input, now).isFirstSubscription;
}

/**
 * Indica se o usuário pode iniciar uma recontratação.
 */
export function isResubscriptionEligible(
  input: SubscriptionAccessInput,
  now: Date = new Date()
): boolean {
  return resolveSubscriptionAccess(
    input,
    now
  ).isResubscriptionEligible;
}

interface CreateStateInput {
  kind: SubscriptionAccessState['kind'];
  reason: SubscriptionAccessReason;
  hasSubscriptionHistory: boolean;
  expiresAt: Date | null;
}

function createState(input: CreateStateInput): SubscriptionAccessState {
  const hasAccess =
    input.kind === 'active' ||
    input.kind === 'canceled_with_access';

  const isExpired = input.kind === 'expired';

  const isFirstSubscription =
    !input.hasSubscriptionHistory &&
    (
      input.kind === 'never_subscribed' ||
      input.kind === 'pending_checkout'
    );

  const isResubscription =
    input.hasSubscriptionHistory &&
    input.kind === 'expired';

  const isResubscriptionEligible = isResubscription;

  const checkoutMode = resolveCheckoutMode({
    kind: input.kind,
    isFirstSubscription,
    isResubscriptionEligible,
  });

  const canCreateCheckout =
    input.kind !== 'pending_checkout' &&
    (
      checkoutMode === 'first_subscription' ||
      checkoutMode === 'resubscription'
    );

  const shouldManageExistingSubscription =
    input.kind === 'active' ||
    input.kind === 'canceled_with_access' ||
    input.kind === 'past_due';

  const requiresBillingResolution =
    input.kind === 'past_due';

  return {
    kind: input.kind,
    reason: input.reason,

    hasAccess,
    isExpired,

    hasSubscriptionHistory: input.hasSubscriptionHistory,
    isFirstSubscription,
    isResubscription,
    isResubscriptionEligible,

    canCreateCheckout,
    checkoutMode,

    shouldManageExistingSubscription,
    requiresBillingResolution,

    expiresAt: cloneDate(input.expiresAt),
  };
}

interface ResolveCheckoutModeInput {
  kind: SubscriptionAccessState['kind'];
  isFirstSubscription: boolean;
  isResubscriptionEligible: boolean;
}

function resolveCheckoutMode(
  input: ResolveCheckoutModeInput
): SubscriptionCheckoutMode {
  if (
    input.isFirstSubscription &&
    (
      input.kind === 'never_subscribed' ||
      input.kind === 'pending_checkout'
    )
  ) {
    return 'first_subscription';
  }

  if (input.isResubscriptionEligible) {
    return 'resubscription';
  }

  return 'not_eligible';
}

function isPendingCheckoutStep(
  lastStep: string | null
): boolean {
  return lastStep !== null && PENDING_CHECKOUT_STEPS.has(lastStep);
}

function isCanceledStatus(
  status: string | null
): boolean {
  return status === 'canceled' || status === 'cancelled';
}

function isExpiredStatus(
  status: string | null
): boolean {
  return status === 'expired';
}

function normalizeStatus(
  value: unknown
): string | null {
  if (typeof value !== 'string') {
    return null;
  }

  const normalized = value.trim().toLowerCase();

  return normalized.length > 0 ? normalized : null;
}

function cloneDate(value: Date | null): Date | null {
  return value === null
    ? null
    : new Date(value.getTime());
}

function isValidDate(value: Date): boolean {
  return !Number.isNaN(value.getTime());
}
